import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  GlassCard,
  SettingsPageHeader,
  Pill,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { BUTTON_PRIMARY } from '@/shared/ui-class';
import {
  SEED_INSTALLED_PLUGINS,
  SEED_PLUGIN_SKILLS,
  type InstalledPluginSeed,
} from '@/versions/v1/shared/settings-seed';
import { formatRelativeTime } from './format-utils';

export function PluginDetailScene() {
  const { pluginId } = useParams<{ pluginId: string }>();
  const navigate = useNavigate();
  const [plugin, setPlugin] = useState<InstalledPluginSeed | null>(() =>
    SEED_INSTALLED_PLUGINS.find((entry) => entry.id === pluginId) ?? null,
  );

  const skills = useMemo(
    () => SEED_PLUGIN_SKILLS.filter((skill) => skill.pluginId === plugin?.marketplaceId),
    [plugin?.marketplaceId],
  );

  if (!plugin) {
    return (
      <SurfacePageLayout width="narrow">
        <SurfacePageBody className="space-y-4">
          <SettingsPageHeader title="Plugin not found" />
          <Link to="/settings/plugins" className="text-sm text-accent-600">
            Back to plugins
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="PluginDetailScene">
        <SurfacePageBody className="space-y-6">
          <div>
            <Link
              to="/settings/plugins"
              className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              <ArrowLeft className="h-4 w-4" />
              Plugins
            </Link>
            <SettingsPageHeader title={plugin.name} description={plugin.description} />
          </div>

          <GlassCard className="space-y-4 p-5">
            <div className="flex flex-wrap items-center gap-2">
              <Pill>v{plugin.version}</Pill>
              <Pill>{plugin.publisher}</Pill>
              {plugin.trusted ? <Pill tone="admin">trusted</Pill> : null}
              <span className="text-xs text-muted">
                Installed {formatRelativeTime(plugin.installedAt)}
              </span>
            </div>
            <label className="flex items-center justify-between gap-4">
              <span className="text-sm font-medium">Plugin enabled</span>
              <input
                type="checkbox"
                checked={plugin.enabled}
                onChange={(event) =>
                  setPlugin((prev) =>
                    prev ? { ...prev, enabled: event.target.checked } : prev,
                  )
                }
              />
            </label>
            {!plugin.trusted ? (
              <button
                type="button"
                className={BUTTON_PRIMARY}
                onClick={() => setPlugin((prev) => (prev ? { ...prev, trusted: true } : prev))}
              >
                Trust plugin
              </button>
            ) : null}
          </GlassCard>

          <GlassCard className="overflow-hidden">
            <div className="border-b border-[var(--glass-border)] px-5 py-4">
              <h2 className="text-base font-medium">Skills</h2>
              <p className="mt-1 text-sm text-muted">
                Skills contributed by this plugin.
              </p>
            </div>
            {skills.length === 0 ? (
              <p className="px-5 py-4 text-sm text-muted">No skills registered.</p>
            ) : (
              <ul className="divide-y divide-[var(--glass-border)]">
                {skills.map((skill) => (
                  <li key={skill.name} className="px-5 py-3">
                    <div className="font-mono text-sm">{skill.name}</div>
                    <p className="mt-0.5 text-sm text-muted">{skill.description}</p>
                  </li>
                ))}
              </ul>
            )}
          </GlassCard>

          <GlassCard className="space-y-3 border-red-200 p-5 dark:border-red-900/50">
            <h2 className="text-base font-medium text-red-700 dark:text-red-300">
              Danger zone
            </h2>
            <button
              type="button"
              className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 dark:border-red-900/60 dark:text-red-400"
              onClick={() => navigate('/settings/plugins')}
            >
              Uninstall plugin
            </button>
          </GlassCard>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
